import { useState } from 'react';
import CanjesBandeja from './CanjesBandeja';
import CierresDeTurnoBandeja from './CierresDeTurnoBandeja';
import RutaVisitasBandeja from './RutaVisitasBandeja';
import VisitasPuestoBandeja from './VisitasPuestoBandeja';

/**
 * La sección donde caen los formularios del circuito de asistencia.
 *
 * Hoy tiene tres solapas. Las dos primeras son DOS DINÁMICAS distintas —la distinción es del
 * usuario—: en la de canje la impulsadora registra la compra de un cliente; en la de impulso
 * reporta lo que puede contar al cerrar, que es todo lo que se puede medir cuando en la tienda no
 * la dejan usar el celular.
 *
 * La tercera no es una dinámica y por eso va al final: son las **visitas del supervisor** (0092),
 * el trabajo de quien recorre las tiendas en vez de estar en una. Cae acá porque es lo mismo que
 * las otras dos —un formulario que llenó alguien en la calle— y separarla en una sección propia
 * habría partido en dos el único lugar donde se mira lo que registró el campo.
 *
 * La sección se llama **«Registros de campo»** desde el 2026-09-05 (antes «Canjes»): adentro ya no
 * hay solo canjes, y el nombre viejo mandaba a buscar el cierre de turno donde no estaba. El
 * usuario lo dio por bueno «por ahora», así que el rótulo puede volver a moverse.
 *
 * Fue un cambio de rótulo y nada más: la ruta sigue siendo `/panel/canjes`, la clave de permiso
 * sigue siendo `canjes`, y las tablas se siguen llamando `canje_records` e `impulso_reports`. La
 * solapa de adentro SÍ se llama «Canjes», y eso es a propósito —*«la que es de Canjes es
 * Canjes»*—: la sección es el cajón, la solapa es la dinámica.
 *
 * **El padding de la sección se pone acá, una sola vez.** Lo tenía cada solapa por su cuenta —o no
 * lo tenía—, así que cambiar de solapa movía el contenido 24 px y el encabezado de la sección
 * aparecía y desaparecía. Un contenedor que salta al cambiar de pestaña se lee como si hubiera
 * cargado otra pantalla, que es justo lo que no pasó.
 */
export default function BandejaDeFormularios() {
  const [tab, setTab] = useState<'canjes' | 'cierres' | 'visitas' | 'puestos'>('canjes');
  /**
   * La solapa activa se marca con TRES cosas —peso, color y borde inferior— y no solo con color:
   * quien no distingue el azul del gris se queda sin saber qué está mirando. El `-mb-px` sube el
   * borde de 2 px encima de la línea del contenedor para que sea una sola línea y no dos.
   *
   * `py-3` no es aire decorativo: son 44 px de alto, que es el área de toque mínima con la que
   * esto se puede usar desde una tablet en la reunión de la mañana.
   */
  const clase = (v: typeof tab) =>
    `-mb-px border-b-2 px-4 py-3 text-sm ${
      tab === v
        ? 'border-blue-600 font-semibold text-blue-700'
        : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'
    }`;
  return (
    <div className="p-6">
      <h1 className="mb-1 text-xl font-semibold text-gray-900">Registros de campo</h1>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        Todo lo que el campo llenó desde el celular, en un solo cajón: los canjes que registró la
        impulsadora, el cierre de turno de la dinámica de impulso, las visitas del supervisor a las
        tiendas y las visitas a puestos dentro de un mercado. Cada solapa es un formulario distinto,
        con sus propios filtros.
      </p>

      {/* Sin `role="tab"` a propósito: ese rol le promete al lector de pantalla que las flechas
          mueven entre solapas, y acá no lo hacen. `aria-current` dice cuál está activa sin
          prometer nada que la pantalla no cumpla. */}
      <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200">
        <button type="button" onClick={() => setTab('canjes')}
          aria-current={tab === 'canjes' ? 'true' : undefined} className={clase('canjes')}>
          Canjes
        </button>
        <button type="button" onClick={() => setTab('cierres')}
          aria-current={tab === 'cierres' ? 'true' : undefined} className={clase('cierres')}>
          Cierres de turno
        </button>
        <button type="button" onClick={() => setTab('visitas')}
          aria-current={tab === 'visitas' ? 'true' : undefined} className={clase('visitas')}>
          Visitas de supervisor
        </button>
        <button type="button" onClick={() => setTab('puestos')}
          aria-current={tab === 'puestos' ? 'true' : undefined} className={clase('puestos')}>
          Visitas a puestos
        </button>
      </div>
      {tab === 'canjes' && <CanjesBandeja />}
      {tab === 'cierres' && <CierresDeTurnoBandeja />}
      {tab === 'visitas' && <RutaVisitasBandeja />}
      {/* Render condicional y NO `hidden`: `hidden` pierde contra cualquier `display` de la hoja
          de estilos, y una solapa que se queda dibujada debajo de la tabla no da error ni aviso.
          Ya pasó dos veces en el panel de seguimiento. */}
      {tab === 'puestos' && <VisitasPuestoBandeja />}
    </div>
  );
}
